import React, { useState, useEffect } from 'react';
import { API_ENDPOINTS } from '../../config/apiRouting';
import './scheduling-components.css';

interface TimeOffRequest {
  requestId: string;
  employeeId: string;
  employeeName: string;
  startDate: string;
  endDate: string;
  reason: string;
  status: 'pending' | 'approved' | 'denied';
  requestedAt: string;
}

interface TimeOffRequestsProps {
  storeId: string;
  enterpriseId: string;
}

export const TimeOffRequests: React.FC<TimeOffRequestsProps> = ({ 
  storeId, 
  enterpriseId 
}) => {
  const [requests, setRequests] = useState<TimeOffRequest[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchTimeOffRequests();
  }, [storeId, enterpriseId]);

  const fetchTimeOffRequests = async () => {
    try {
      setLoading(true);
      const response = await fetch(
        `${API_ENDPOINTS.scheduleRequests()}?storeId=${storeId}&enterpriseId=${enterpriseId}`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('authToken') || ''}`,
            'Content-Type': 'application/json'
          }
        }
      );
      if (response.ok) {
        const data = await response.json();
        setRequests(data.scheduleRequests || []);
      }
    } catch (error) {
      console.error('Error fetching time off requests:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (requestId: string) => {
    try {
      await fetch(`${API_ENDPOINTS.scheduleRequest(requestId)}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken') || ''}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ status: 'approved' })
      });
      fetchTimeOffRequests();
    } catch (error) {
      console.error('Error approving request:', error);
    }
  };

  const handleDeny = async (requestId: string) => {
    try {
      await fetch(`${API_ENDPOINTS.scheduleRequest(requestId)}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken') || ''}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ status: 'denied' })
      });
      fetchTimeOffRequests();
    } catch (error) {
      console.error('Error denying request:', error);
    }
  };

  if (loading) {
    return <div className="loading">Loading time off requests...</div>;
  }

  return (
    <div className="time-off-requests">
      <div className="page-header">
        <h2>Time Off Requests</h2>
        <div className="request-stats">
          <span className="stat">
            Pending: {requests.filter(r => r.status === 'pending').length}
          </span>
        </div>
      </div>

      {requests.length === 0 ? (
        <div className="empty-state">
          <h3>No Time Off Requests</h3>
          <p>Employee time off requests will appear here for approval.</p>
        </div>
      ) : (
        <div className="requests-list">
          {requests.map((request) => (
            <div key={request.requestId} className="request-card">
              <div className="request-header">
                <h3>{request.employeeName}</h3>
                <span className={`status ${request.status}`}>
                  {request.status.charAt(0).toUpperCase() + request.status.slice(1)}
                </span>
              </div>
              <div className="request-details">
                <p><strong>Dates:</strong> {request.startDate} to {request.endDate}</p>
                <p><strong>Reason:</strong> {request.reason}</p>
                <p><strong>Requested:</strong> {new Date(request.requestedAt).toLocaleDateString()}</p>
              </div>
              {request.status === 'pending' && (
                <div className="request-actions">
                  <button 
                    className="btn btn-success"
                    onClick={() => handleApprove(request.requestId)}
                  >
                    Approve
                  </button>
                  <button 
                    className="btn btn-danger"
                    onClick={() => handleDeny(request.requestId)}
                  >
                    Deny
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};